import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Plus,
  Calendar,
  Lock,
  Unlock,
  RefreshCw,
  Check,
  Settings,
  Clock,
  Save,
  Globe
} from 'lucide-react';
import {
  getAllPeriods,
  createPeriodsForYear,
  openPeriod,
  closePeriod,
  getGlobalSettings,
  updateGlobalSettings
} from '../../api/fiscalPeriods';
import { useAuth } from '../../contexts/AuthContext';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PERIOD_TYPES = [
  {
    value: 'forever',
    label: 'Forever (All Time)',
    description: 'No date filtering - show all historical data'
  },
  { value: 'daily', label: 'Daily', description: 'Reset every day at midnight' },
  { value: 'weekly', label: 'Weekly', description: 'Monday to Sunday each week' },
  { value: 'monthly', label: 'Monthly', description: 'First to last day of each month' },
  { value: 'quarterly', label: 'Quarterly', description: 'Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec' },
  { value: 'half_yearly', label: 'Half-Yearly', description: 'Jan-Jun (H1) or Jul-Dec (H2)' },
  { value: 'yearly', label: 'Yearly', description: 'Full fiscal year based on start month' },
  { value: 'custom', label: 'Custom', description: 'Manually specify date range' }
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const FiscalPeriodsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshContext, periodRange: contextPeriodRange, module } = useFiscalPeriod();

  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);

  // Global settings state
  const [globalSettings, setGlobalSettings] = useState({
    period_type: 'forever',
    custom_start_date: '',
    custom_end_date: '',
    fiscal_year_start_month: 1,
    fiscal_year_start_day: 1,
    show_period_badge_on_dashboard: true,
    allow_users_to_override: true,
    auto_manage_periods: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);

  const canEditGlobalSettings = ['super_admin', 'system_admin'].includes(user?.role);

  useEffect(() => {
    fetchPeriods();
    fetchGlobalSettings();
  }, [module]);

  const fetchGlobalSettings = async () => {
    try {
      const response = await getGlobalSettings(module || 'retail');
      if (response.success) {
        setGlobalSettings({
          ...response.data,
          custom_start_date: response.data.custom_start_date?.split('T')[0] || '',
          custom_end_date: response.data.custom_end_date?.split('T')[0] || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
    }
  };

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const data = await getAllPeriods();
      setPeriods(data || []);
    } catch (error) {
      toast.error('Failed to fetch fiscal periods');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setGlobalSettings((prev) => ({ ...prev, [field]: value }));
    setSettingsChanged(true);
  };

  const handleSaveGlobalSettings = async () => {
    if (!canEditGlobalSettings) {
      toast.error('You do not have permission to change global settings');
      return;
    }

    // Validate custom dates
    if (globalSettings.period_type === 'custom') {
      if (!globalSettings.custom_start_date || !globalSettings.custom_end_date) {
        toast.error('Please specify both start and end dates for custom period');
        return;
      }
      if (new Date(globalSettings.custom_start_date) > new Date(globalSettings.custom_end_date)) {
        toast.error('Start date must be before end date');
        return;
      }
    }

    setSavingSettings(true);
    try {
      const response = await updateGlobalSettings(globalSettings, module || 'retail');
      if (response.success) {
        toast.success(
          `${(module || 'retail').charAt(0).toUpperCase() + (module || 'retail').slice(1)} fiscal settings updated successfully`
        );
        setSettingsChanged(false);
        // Refresh context to update all components using the period
        refreshContext();
      } else {
        toast.error(response.message || 'Failed to update settings');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update global settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGenerate = async () => {
    if (!generateYear) {
      toast.error('Please enter a year');
      return;
    }

    setGenerating(true);
    try {
      await createPeriodsForYear(generateYear);
      toast.success(`Periods for ${generateYear} generated successfully`);
      setShowGenerateModal(false);
      fetchPeriods();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate periods');
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenPeriod = async (id) => {
    if (
      !confirm(
        'Are you sure you want to open this period? Any currently open period will be closed.'
      )
    )
      return;
    try {
      await openPeriod(id);
      toast.success('Period opened successfully');
      fetchPeriods();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to open period');
    }
  };

  const handleClosePeriod = async (id) => {
    if (!confirm('Are you sure you want to close this period? Ensure all entries are posted.'))
      return;
    try {
      await closePeriod(id);
      toast.success('Period closed successfully');
      fetchPeriods();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to close period');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700',
      locked: 'bg-red-100 text-red-700',
      not_started: 'bg-yellow-100 text-yellow-700'
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}
      >
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-theme-primary">Fiscal Periods</h1>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  module === 'wholesale'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {(module || 'retail').toUpperCase()}
              </span>
            </div>
            <p className="text-sm text-theme-muted">
              Manage {module || 'retail'} financial accounting periods and global date filtering
            </p>
          </div>
        </div>
      </div>

      {/* Global Period Settings Card */}
      <div className="bg-theme-secondary rounded-lg shadow-theme-md border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme bg-theme-tertiary flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-accent-gold" />
            <div>
              <h2 className="text-lg font-semibold text-theme-primary">
                {(module || 'retail').charAt(0).toUpperCase() + (module || 'retail').slice(1)}{' '}
                Period Settings
              </h2>
              <p className="text-sm text-theme-muted">
                Configure how data is filtered across {module || 'retail'} dashboards and reports
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Current Period Preview */}
          <div className="p-4 bg-theme-tertiary rounded-lg border border-theme">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-accent-gold" />
              <span className="text-sm font-medium text-theme-primary">Current Active Period</span>
            </div>
            <div className="text-2xl font-bold text-accent-gold">
              {contextPeriodRange?.period_label || 'All Time'}
            </div>
            {contextPeriodRange?.start_date && (
              <div className="text-sm text-theme-muted mt-1">
                {new Date(contextPeriodRange.start_date).toLocaleDateString()} —{' '}
                {new Date(contextPeriodRange.end_date).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Period Type Selection */}
          <div>
            <label className="block text-sm font-medium text-theme-primary mb-3">Period Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {PERIOD_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleSettingsChange('period_type', type.value)}
                  disabled={!canEditGlobalSettings}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    globalSettings.period_type === type.value
                      ? 'border-accent-gold bg-accent-gold/10'
                      : 'border-theme hover:border-accent-gold/50 bg-theme-secondary'
                  } ${!canEditGlobalSettings ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className="font-medium text-theme-primary text-sm">{type.label}</div>
                  <div className="text-xs text-theme-muted mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range (shown when custom selected) */}
          {globalSettings.period_type === 'custom' && (
            <div className="p-4 bg-theme-tertiary rounded-lg space-y-4">
              <h3 className="font-medium text-theme-primary">Custom Date Range</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={globalSettings.custom_start_date}
                    onChange={(e) => handleSettingsChange('custom_start_date', e.target.value)}
                    disabled={!canEditGlobalSettings}
                    className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={globalSettings.custom_end_date}
                    onChange={(e) => handleSettingsChange('custom_end_date', e.target.value)}
                    disabled={!canEditGlobalSettings}
                    className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Fiscal Year Start (shown for yearly) */}
          {globalSettings.period_type === 'yearly' && (
            <div className="p-4 bg-theme-tertiary rounded-lg space-y-4">
              <h3 className="font-medium text-theme-primary">Fiscal Year Start</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Start Month
                  </label>
                  <select
                    value={globalSettings.fiscal_year_start_month}
                    onChange={(e) =>
                      handleSettingsChange('fiscal_year_start_month', parseInt(e.target.value))
                    }
                    disabled={!canEditGlobalSettings}
                    className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                  >
                    {MONTHS.map((month, idx) => (
                      <option key={month} value={idx + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Start Day
                  </label>
                  <input
                    type="number"
                    value={globalSettings.fiscal_year_start_day}
                    onChange={(e) =>
                      handleSettingsChange('fiscal_year_start_day', parseInt(e.target.value))
                    }
                    min="1"
                    max="31"
                    disabled={!canEditGlobalSettings}
                    className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Display Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={globalSettings.show_period_badge_on_dashboard}
                onChange={(e) =>
                  handleSettingsChange('show_period_badge_on_dashboard', e.target.checked)
                }
                disabled={!canEditGlobalSettings}
                className="w-4 h-4 text-accent-gold rounded border-gray-300"
              />
              <span className="text-sm text-theme-primary">Show period badge on dashboard</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={globalSettings.allow_users_to_override}
                onChange={(e) => handleSettingsChange('allow_users_to_override', e.target.checked)}
                disabled={!canEditGlobalSettings}
                className="w-4 h-4 text-accent-gold rounded border-gray-300"
              />
              <span className="text-sm text-theme-primary">
                Allow users to override period on individual reports
              </span>
            </label>
          </div>

          {/* Save Button */}
          {canEditGlobalSettings && (
            <div className="flex justify-end pt-4 border-t border-theme">
              <button
                onClick={handleSaveGlobalSettings}
                disabled={savingSettings || !settingsChanged}
                className="flex items-center gap-2 px-6 py-2 bg-accent-gold text-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}

          {!canEditGlobalSettings && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              Only Super Admin and System Admin can change global fiscal settings.
            </p>
          )}
        </div>
      </div>

      {/* Accounting Periods Section */}
      <div className="bg-theme-secondary rounded-lg shadow-theme-md border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme bg-theme-tertiary">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-accent-gold" />
              <div>
                <h2 className="text-lg font-semibold text-theme-primary">Accounting Periods</h2>
                <p className="text-sm text-theme-muted">
                  Monthly periods for journal entries and financial statements
                </p>
              </div>
            </div>
            {!globalSettings.auto_manage_periods && (
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchPeriods}
                  className="p-2 border border-theme rounded-lg hover:bg-theme-tertiary"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="flex items-center gap-2 bg-accent-gold hover:bg-accent-goldDark text-black px-4 py-2 rounded-lg font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Generate Periods
                </button>
              </div>
            )}
          </div>

          {/* Auto/Manual Period Management Toggle */}
          <div className="bg-theme-secondary rounded-lg p-4 border border-theme">
            <div className="flex items-start gap-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="auto-manage-toggle"
                  checked={globalSettings.auto_manage_periods || false}
                  onChange={(e) => handleSettingsChange('auto_manage_periods', e.target.checked)}
                  disabled={!canEditGlobalSettings}
                  className="w-4 h-4 text-accent-gold rounded border-gray-300 focus:ring-accent-gold focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="auto-manage-toggle"
                  className="font-medium text-theme-primary cursor-pointer"
                >
                  Automatic Period Management
                </label>
                <p className="text-sm text-theme-muted mt-1">
                  {globalSettings.auto_manage_periods
                    ? 'System automatically generates, opens, and closes periods based on current date. Scheduler runs every 6 hours.'
                    : 'Periods are managed manually. You need to generate, open, and close periods yourself.'}
                </p>
                {!canEditGlobalSettings && (
                  <p className="text-xs text-amber-600 mt-2">
                    Only Super Admin and System Admin can change this setting.
                  </p>
                )}
              </div>
            </div>

            {globalSettings.auto_manage_periods && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-2 text-sm text-green-700 dark:text-green-400">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Auto-management is active</p>
                    <p className="mt-1">The system will:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Automatically generate periods for current and next year</li>
                      <li>Open the period matching today's date</li>
                      <li>Close past periods automatically</li>
                      <li>Run checks every 6 hours</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Periods Table - Only show if auto-management is disabled */}
        {!globalSettings.auto_manage_periods && (
          <>
            {loading ? (
              <LoadingSpinner message="Loading fiscal periods..." className="py-12" />
            ) : periods.length === 0 ? (
              <div className="p-8 text-center text-theme-muted">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No fiscal periods found</p>
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="mt-4 px-4 py-2 bg-accent-gold text-black rounded-lg font-medium"
                >
                  Generate First Year
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-theme-tertiary">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-theme-muted uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-theme-muted uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-theme-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-theme-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-theme">
                    {periods.map((period) => (
                      <tr key={period.id} className="hover:bg-theme-tertiary">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-theme-primary">
                            {period.period_name}
                          </div>
                          <div className="text-sm text-theme-muted">Year: {period.fiscal_year}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-theme-muted">
                            {new Date(period.start_date).toLocaleDateString()} -{' '}
                            {new Date(period.end_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {getStatusBadge(period.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          {period.status === 'not_started' && (
                            <button
                              onClick={() => handleOpenPeriod(period.id)}
                              className="text-accent-gold hover:text-accent-goldDark flex items-center justify-center gap-1 mx-auto"
                            >
                              <Unlock className="w-4 h-4" /> Open
                            </button>
                          )}
                          {period.status === 'open' && (
                            <button
                              onClick={() => handleClosePeriod(period.id)}
                              className="text-red-600 hover:text-red-900 flex items-center justify-center gap-1 mx-auto"
                            >
                              <Lock className="w-4 h-4" /> Close
                            </button>
                          )}
                          {period.status === 'closed' && (
                            <span className="text-theme-muted flex items-center justify-center gap-1">
                              <Lock className="w-4 h-4" /> Closed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Auto-management info when enabled */}
        {globalSettings.auto_manage_periods && (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <Clock className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-theme-primary mb-2">
              Auto-Management Enabled
            </h3>
            <p className="text-theme-muted max-w-md mx-auto">
              Periods are being automatically managed by the system. Manual generation and controls
              are disabled. The system checks and updates periods every 6 hours based on the current
              date.
            </p>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-secondary rounded-lg shadow-lg w-full max-w-sm">
            <div className="p-4 border-b border-theme">
              <h2 className="text-lg font-semibold text-theme-primary">Generate Fiscal Periods</h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Fiscal Year
                </label>
                <input
                  type="number"
                  value={generateYear}
                  onChange={(e) => setGenerateYear(e.target.value)}
                  className="w-full border border-theme rounded-lg px-3 py-2 bg-theme-secondary text-theme-primary"
                  min="2000"
                  max="2100"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="flex-1 px-4 py-2 border border-theme rounded-lg hover:bg-theme-tertiary text-theme-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex-1 bg-accent-gold hover:bg-accent-goldDark text-black px-4 py-2 rounded-lg disabled:opacity-50 font-medium"
                >
                  {generating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiscalPeriodsPage;
